import React from 'react';
import './MessageCard.css';

/**
 * MessageCard Component
 * Displays a single message in the inbox with subject, sender, and email preview
 *
 * @param {Object} props
 * @param {Object} props.message - Message data object
 * @param {Function} props.onViewMessage - Callback when message is clicked
 */
const MessageCard = ({ message, onViewMessage }) => {
  const handleClick = () => {
    if (onViewMessage) {
      onViewMessage(message);
    }
  };

  // Format date to readable string
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    }
  };

  // Get email preview text (first 150 chars of text body or html body stripped)
  const getEmailPreview = () => {
    if (message.text_body) {
      return message.text_body.substring(0, 150) + (message.text_body.length > 150 ? '...' : '');
    }
    if (message.html_body) {
      // Strip HTML tags
      const stripped = message.html_body.replace(/<[^>]*>/g, '');
      return stripped.substring(0, 150) + (stripped.length > 150 ? '...' : '');
    }
    return 'No content';
  };

  // Get status indicator class
  const getStatusClass = () => {
    if (!message.processed) {
      return 'message-unread';
    }
    return 'message-read';
  };

  return (
    <div
      className={`message-card ${getStatusClass()}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
    >
      {/* Header with sender and date */}
      <div className="message-card-header">
        <div className="sender-info">
          <div className="sender-avatar">
            {message.from_name?.charAt(0) || message.from_email?.charAt(0) || '?'}
          </div>
          <div className="sender-details">
            <h3 className="sender-name">
              {message.from_name || message.from_email}
            </h3>
            <p className="sender-email">{message.from_email}</p>
          </div>
        </div>
        <div className="message-date">
          {formatDate(message.received_at)}
        </div>
      </div>

      {/* Subject line */}
      <div className="message-subject">
        {!message.processed && <span className="unread-dot"></span>}
        <span className="subject-text">{message.subject || '(No Subject)'}</span>
        {message.has_attachments && (
          <span className="attachment-indicator" title="Has attachments">
            📎
          </span>
        )}
      </div>

      {/* Email preview */}
      <div className="message-preview">{getEmailPreview()}</div>

      {/* Message metadata */}
      <div className="message-meta">
        <span className="message-type-badge">{message.message_type.replace('_', ' ')}</span>
        {message.attachment_count > 0 && (
          <span className="attachment-count">
            {message.attachment_count} attachment{message.attachment_count > 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  );
};

export default MessageCard;
